import React, { useState, useEffect } from 'react';
import { Button, ClearableInput } from '@rio-cloud/rio-uikit';
import { Step } from '../../../../models/step';

interface StepFormProps {
  step: Step;
  onSave: (stepId: string, hoursElapsed: number) => Promise<boolean>;
  onCancel: () => void;
}

const StepForm: React.FC<StepFormProps> = ({
  step,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    id: step.id,
    name: step.name,
    elapsedHours: step.elapsedHours,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData({
      id: step.id,
      name: step.name,
      elapsedHours: step.elapsedHours,
    });
  }, [step]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.elapsedHours || formData.elapsedHours <= 0) {
      newErrors.elapsedHours = 'Tempo decorrido deve ser maior que zero';
    }

    if (formData.elapsedHours > 999) {
      newErrors.elapsedHours = 'Tempo decorrido não pode ser maior que 999 horas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const success = await onSave(formData.id, formData.elapsedHours);
      if (success) {
        onCancel();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            Nome da Etapa
          </label>
          <input
            type="text"
            value={formData.name}
            disabled
            className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-md bg-gray-100 dark:bg-neutral-900 text-gray-600 dark:text-neutral-400"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
            O nome da etapa não pode ser alterado
          </p>
        </div>

        <div>
          <label className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            Tempo Decorrido (horas) *
          </label>
          <input
            type="number"
            min="1"
            max="999"
            value={formData.elapsedHours}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              elapsedHours: parseInt(e.target.value) || 0 
            }))}
            placeholder="Digite o tempo em horas"
            className={`w-full px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.elapsedHours ? 'border-red-500' : 'border-gray-300 dark:border-neutral-700'
            }`}
          />
          {errors.elapsedHours && (
            <p className="mt-1 text-red-600">{errors.elapsedHours}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6">
        <Button
          bsStyle={Button.DEFAULT}
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          bsStyle={Button.PRIMARY}
          type="submit"
          disabled={loading}
          iconName={loading ? "rioglyph-spinner" : undefined}
        >
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  );
};

export default StepForm;
